import { trackAnalyticsEvent } from 'util/analytics';
import React, { ReactElement, useEffect, useRef } from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { Event } from 'services/api';
import { MaterialSymbol } from '../../shared';
import { todayCardState } from './feedEvents';
import {
  FEED_COLORS,
  FEED_FONT,
  FEED_SHADOW,
  POSTER_SHADOW,
  feedLocation,
  formatCheckedInAt,
  formatFeedTimeRange,
} from './feedTokens';

export type NowCardAction = 'check-in' | 'checked-in' | 'check-in-attendees';

export interface EventCardNowProps {
  event: Event;
  hosting: boolean;
  position: number;
  loading?: boolean;
  onOpen: () => void;
  onCheckIn: () => void;
}

const titleSx = {
  m: 0,
  width: '100%',
  minHeight: '52px',
  fontFamily: FEED_FONT,
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '26px',
  color: FEED_COLORS.textPrimary,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;

export const EventCardNow = ({
  event,
  hosting,
  position,
  loading = false,
  onOpen,
  onCheckIn,
}: EventCardNowProps): ReactElement => {
  const live = todayCardState(event) === 'now';
  const checkedIn = Boolean(event.checkedIn);
  const location = feedLocation(event);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    let reported = false;
    const observer = new IntersectionObserver(
      entries => {
        if (reported) return;
        const visible = entries.some(
          entry => entry.isIntersecting && entry.intersectionRatio >= 0.5,
        );
        if (!visible) return;
        reported = true;
        trackAnalyticsEvent({
          name: 'today_event_card_impression',
          props: {
            event_id: event.id,
            position,
            event_state: live ? 'now' : 'upcoming_today',
          },
        });
        observer.disconnect();
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [event.id, live, position]);

  const action: NowCardAction = hosting
    ? 'check-in-attendees'
    : checkedIn
      ? 'checked-in'
      : 'check-in';

  return (
    <Box
      ref={rootRef}
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: FEED_COLORS.surface,
        border: `1.5px solid ${FEED_COLORS.border}`,
        borderRadius: '16px',
        boxShadow: FEED_SHADOW,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ButtonBase
        onClick={onOpen}
        disableRipple
        aria-label={event.name}
        sx={{
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '3 / 4',
            flexShrink: 0,
            overflow: 'hidden',
            backgroundColor: FEED_COLORS.placeholder,
          }}
        >
          {event.flyerImageUrl && (
            <>
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${event.flyerImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(18px)',
                  transform: 'scale(1.08)',
                }}
              />
              <Box
                component="img"
                src={event.flyerImageUrl}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  boxShadow: POSTER_SHADOW,
                }}
              />
            </>
          )}
          {hosting && (
            <Box
              sx={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                backgroundColor: FEED_COLORS.chipBg,
                borderRadius: '8px',
                px: '8px',
                py: '4px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: FEED_FONT,
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: FEED_COLORS.textPrimary,
                }}
              >
                Hosting
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            p: '12px',
            width: '100%',
          }}
        >
          <Typography component="p" sx={titleSx}>
            {event.name}
          </Typography>
          {live && !hosting ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: FEED_COLORS.liveBg,
                borderRadius: '8px',
                px: '7px',
                py: '3px',
              }}
            >
              <Box
                sx={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '9999px',
                  backgroundColor: FEED_COLORS.liveText,
                }}
              />
              <Typography
                sx={{
                  fontFamily: FEED_FONT,
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: 'normal',
                  letterSpacing: '0.6px',
                  color: FEED_COLORS.liveText,
                }}
              >
                HAPPENING NOW
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                m: 0,
                width: '100%',
                fontFamily: FEED_FONT,
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '18px',
                color: FEED_COLORS.textSecondary,
              }}
            >
              {formatFeedTimeRange(event.startTime, event.endTime)}
            </Typography>
          )}
          {location && (
            <Typography
              sx={{
                m: 0,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: FEED_FONT,
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '18px',
                color: FEED_COLORS.textSecondary,
              }}
            >
              {location}
            </Typography>
          )}
        </Box>
      </ButtonBase>

      <Box sx={{ p: '12px', pt: 0 }}>
        {action === 'checked-in' ? (
          <Box
            role="status"
            sx={{
              height: '44px',
              width: '100%',
              borderRadius: '8px',
              backgroundColor: FEED_COLORS.successTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              px: '16px',
            }}
          >
            <MaterialSymbol name="check" size={18} color={FEED_COLORS.success} />
            <Typography
              sx={{
                fontFamily: FEED_FONT,
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
                color: FEED_COLORS.success,
              }}
            >
              {formatCheckedInAt(event.checkedInAt)}
            </Typography>
          </Box>
        ) : (
          <ButtonBase
            component="button"
            type="button"
            onClick={loading ? undefined : onCheckIn}
            aria-busy={loading}
            aria-label={
              action === 'check-in-attendees' ? 'Check in attendees' : 'Check in'
            }
            disableRipple
            sx={{
              width: '100%',
              minHeight: '44px',
              borderRadius: '8px',
              backgroundColor: FEED_COLORS.gold,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              px: '24px',
              py: '11px',
              fontFamily: FEED_FONT,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              whiteSpace: 'nowrap',
              color: FEED_COLORS.textPrimary,
              cursor: loading ? 'progress' : 'pointer',
              '&:hover': { backgroundColor: FEED_COLORS.goldHover },
              '&:focus-visible': {
                outline: `2px solid ${FEED_COLORS.textPrimary}`,
                outlineOffset: '2px',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={18} thickness={5} sx={{ color: 'inherit' }} />
            ) : (
              <MaterialSymbol
                name="qr_code_scanner"
                size={18}
                color={FEED_COLORS.textPrimary}
              />
            )}
            {action === 'check-in-attendees' ? 'Check in attendees' : 'Check in'}
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

export const EventCardNowSkeleton = (): ReactElement => (
  <Box
    sx={{
      width: '100%',
      borderRadius: '16px',
      border: `1.5px solid ${FEED_COLORS.border}`,
      overflow: 'hidden',
      backgroundColor: FEED_COLORS.surface,
    }}
  >
    <Box
      sx={{
        width: '100%',
        aspectRatio: '3 / 4',
        backgroundColor: FEED_COLORS.chipBg,
      }}
    />
    <Box sx={{ p: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ height: '18px', width: '70%', backgroundColor: FEED_COLORS.chipBg }} />
      <Box sx={{ height: '44px', borderRadius: '8px', backgroundColor: FEED_COLORS.chipBg }} />
    </Box>
  </Box>
);
